import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { filterFrag } from '../modules/content/service/types/filter.types';
import {
    palliativePeriodStatisticsFrag,
    procedureStatisticsFrag,
    workersStatisticsFrag,
    worksStatisticsFrag,
} from './types/statistics.gql-types';
import { ProcedureStatisticsReportsGetInput } from '@libs/types/src/services/input-type/statistics/procedure-statistics-reports.get-input';
import { ProcedureStatisticsReportsUpsertInput } from '@libs/types/src/services/input-type/statistics/procedure-statistics-reports.upsert-input';
import { ProcedureStatisticReportObject } from '@libs/types/src/services/object-type/statistics/procedure-statistic-report.object';
import { OkObject } from '@it2go/types';
import { ProcedureStatisticsObject } from '@libs/types/src/services/object-type/statistics/procedure-statistics.object';
import { ServiceStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/service-statistics-get.input';
import { AllowStatisticObject } from '@libs/types/src/services/object-type/statistics/allow-statistic.object';
import { AllowStatisticUpsertInput } from '@libs/types/src/services/input-type/statistics/allow-statistic.upsert-input';
import { AllowStatisticGetInput } from '@libs/types/src/services/input-type/statistics/allow-statistic.get-input';
import { ProcedureStatisticsReportsDeleteInput } from '@libs/types/src/services/input-type/statistics/procedure-statistics-reports.delete-input';
import { ServiceStatisticsWorksObject } from '@libs/types/src/services/object-type/statistics/service-statistics-works.object';
import { ServiceStatisticsWorkersObject } from '@libs/types/src/services/object-type/statistics/service-statistics-workers.object';
import { PalliativePeriodProceduresObject } from 'packages/types/src/services/object-type/statistics/palliative-period-procedures-statistics.object';
import { ServiceStatisticsProceduresGetInput } from '@libs/types/src/services/input-type/statistics/service-statistics-procedures-get.input';
import { ServiceStatisticsWorksGetInput } from '@libs/types/src/services/input-type/statistics/service-statistics-works-get.input';
import { FolderStatisticsObject } from '@libs/types/src/services/object-type/statistics/folder-statistics.object';
import { ServiceStatisticsFoldersGetInput } from '@libs/types/src/services/input-type/statistics/service-statistics-folders-get.input';
import { MedicalPermanentResidenceStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/medical-permanent-residence-statistics-get.input';
import { MedicalPermanentResidenceStatisticsObject } from '@libs/types/src/services/object-type/statistics/medical-permanent-residence-statistics.object';
import { SocialServiceStatisticsFoldersGetInput } from '@libs/types/src/services/input-type/statistics/social-service-statistics-folders-get.input';
import { SocialServiceFolderStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-service-folder-statistics.object';
import { SocialServiceStatisticsFinancialDepositClientGetInput } from '@libs/types/src/services/input-type/statistics/social-service-statistics-financial-deposit-client-get.input';
import { SocialServiceFinancialDepositClientStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-service-financial-deposit-client-statistics.object';
import { SocialServiceStatisticsFinancialDepositsClientsSummaryGetInput } from '@libs/types/src/services/input-type/statistics/social-service-statistics-financial-deposits-clients-summary-get.input';
import { SocialServiceFinancialDepositsClientsSummaryStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-service-financial-deposits-clients-summary-statistics.object';
import { SocialServiceStatisticsBillingGetInput } from '@libs/types/src/services/input-type/statistics/social-service-statistics-billing-get.input';
import { SocialServiceBillingStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-service-billing-statistics.object';
import { SocialServiceBillingItemStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-service-billing-item-statistics.object';
import { SocialWorkStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-work-statistics-get.input';
import { SocialWorkStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-work-statistics.object';
import { SocialBmStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-bm-statistics-get.input';
import { SocialBmStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-bm-statistics.object';
import { SocialBmCalculatedDataObject } from '@libs/types/src/services/object-type/statistics/social-bm-calculated-data.object';
import { SocialWorkerEducationStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-worker-education-statistics-get.input';
import { SocialWorkerEducationStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-worker-education-statistics.object';
import { SocialWarehouseMaterialStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-warehouse-material-statistics-get.input';
import { SocialWarehouseMaterialStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-warehouse-material-statistics.object';
import { SocialDependenceStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-dependence-statistics-get.input';
import { SocialDependenceStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-dependence-statistics.object';
import { SocialDietStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-diet-statistics-get.input';
import { SocialDietStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-diet-statistics.object';
import { SocialFolderOverviewStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-folder-overview-statistics-get.input';
import { SocialFolderOverviewStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-folder-overview-statistics.object';
import { SocialEmploymentStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-employment-statistics-get.input';
import { SocialEmploymentStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-employment-statistics.object';
import { SocialMpsvStatisticsGetInput } from '@libs/types/src/services/input-type/statistics/social-mpsv-statistics-get.input';
import { SocialMpsvWorkerStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-mpsv-worker-statistics.object';
import { SocialMpsvClientStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-mpsv-client-statistics.object';
import { SocialMpsvAnnualReportStatisticsObject } from '@libs/types/src/services/object-type/statistics/social-mpsv-annual-report-statistics.object';
import { StatisticsDescriptionCreateInput } from '@libs/types/src/services/input-type/statistics/statistics-description-create.input';
import { StatisticsDescriptionUpdateInput } from '@libs/types/src/services/input-type/statistics/statistics-description-update.input';
import {
    StatisticsDescriptionGridObject,
    StatisticsDescriptionObject,
} from '@libs/types/src/services/object-type/statistics/statistics-description.object';
import { SocialDeceasedClientInvitationExportInput } from '@libs/types/src/services/input-type/statistics/social-deceased-client-invitation-export.input';
import { FileDownloadObject } from '@libs/types/src/common/object-type/file-download.object';
import { DashboardFinancialBalanceInput } from '@libs/types/src/services/input-type/dashboard/dashboard-financial-balance.input';
import { DashboardPerformedWorkInput } from '@libs/types/src/services/input-type/dashboard/dashboard-performed-work.input';
import { DashboardWorkedHoursInput } from '@libs/types/src/services/input-type/dashboard/dashboard-worked-hours.input';
import { DashboardWorkersWorkedHoursInput } from '@libs/types/src/services/input-type/dashboard/dashboard-workers-worked-hours.input';
import { DashboardFinancialBalanceObject } from '@libs/types/src/services/object-type/dashboard/dashboard-financial-balance.object';
import { DashboardPerformedWorkObject } from '@libs/types/src/services/object-type/dashboard/dashboard-performed-work.object';
import { DashboardWorkedHoursObject } from '@libs/types/src/services/object-type/dashboard/dashboard-worked-hours.object';
import { DashboardWorkersWorkedHoursObject } from '@libs/types/src/services/object-type/dashboard/dashboard-workers-worked-hours.object';

@Injectable({ providedIn: 'root' })
export class StatisticsGqlService extends GqlServiceV2 {
    public async getStatisticsDescriptions(
        input: GridInput | null,
        fragment: string,
    ) {
        return this.query<StatisticsDescriptionGridObject>(
            `
                query($input: GridInput!) {
                    statisticsDescription {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createStatisticsDescription(
        input: StatisticsDescriptionCreateInput,
        fragment: string,
    ) {
        return this.mutate<StatisticsDescriptionObject>(
            `
                mutation($input: StatisticsDescriptionCreateInput!) {
                    statisticsDescription {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async updateStatisticsDescription(
        input: StatisticsDescriptionUpdateInput,
        fragment: string,
    ) {
        return this.mutate<StatisticsDescriptionObject>(
            `
                mutation($input: StatisticsDescriptionUpdateInput!) {
                    statisticsDescription {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteStatisticsDescription(
        input: IdInput,
        fragment: string = 'ok',
    ) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    statisticsDescription {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getProcedureStatistics(
        input: ServiceStatisticsProceduresGetInput,
        fragment: string = procedureStatisticsFrag,
    ) {
        return this.query<ProcedureStatisticsObject[]>(
            `
                query($input: ServiceStatisticsProceduresGetInput!) {
                    serviceStatistics {
                        procedures(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getFolderStatistics(
        input: ServiceStatisticsFoldersGetInput,
        fragment: string,
    ) {
        return this.query<FolderStatisticsObject[]>(
            `
                query($input: ServiceStatisticsFoldersGetInput!) {
                    serviceStatistics {
                        folders(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getMedicalPermanentResidenceStatistics(
        input: MedicalPermanentResidenceStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<MedicalPermanentResidenceStatisticsObject[]>(
            `
                query($input: MedicalPermanentResidenceStatisticsGetInput!) {
                    serviceStatistics {
                        medicalPermanentResidences(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialFolderStatistics(
        input: SocialServiceStatisticsFoldersGetInput,
        fragment: string,
    ) {
        return this.query<SocialServiceFolderStatisticsObject[]>(
            `
                query($input: SocialServiceStatisticsFoldersGetInput!) {
                    serviceStatistics {
                        socialFolders(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialFolderOverviewStatistics(
        input: SocialFolderOverviewStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialFolderOverviewStatisticsObject[]>(
            `
                query($input: SocialFolderOverviewStatisticsGetInput!) {
                    serviceStatistics {
                        socialFolderOverview(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialEmploymentStatistics(
        input: SocialEmploymentStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialEmploymentStatisticsObject[]>(
            `
                query($input: SocialEmploymentStatisticsGetInput!) {
                    serviceStatistics {
                        socialEmployments(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialBillingStatistics(
        input: SocialServiceStatisticsBillingGetInput,
        fragment: string,
    ) {
        return this.query<SocialServiceBillingStatisticsObject[]>(
            `
                query($input: SocialServiceStatisticsBillingGetInput!) {
                    serviceStatistics {
                        socialBilling(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialBillingItemsStatistics(
        input: SocialServiceStatisticsBillingGetInput,
        fragment: string,
    ) {
        return this.query<SocialServiceBillingItemStatisticsObject[]>(
            `
                query($input: SocialServiceStatisticsBillingGetInput!) {
                    serviceStatistics {
                        socialBillingItems(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialWorkStatistics(
        input: SocialWorkStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialWorkStatisticsObject[]>(
            `
                query($input: SocialWorkStatisticsGetInput!) {
                    serviceStatistics {
                        socialWork(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDashboardFinancialBalance(
        input: DashboardFinancialBalanceInput,
        fragment: string,
    ) {
        return this.query<DashboardFinancialBalanceObject[]>(
            `
                query($input: DashboardFinancialBalanceInput!) {
                    serviceStatistics {
                        dashboardFinancialBalance(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDashboardPerformedWork(
        input: DashboardPerformedWorkInput,
        fragment: string,
    ) {
        return this.query<DashboardPerformedWorkObject[]>(
            `
                query($input: DashboardPerformedWorkInput!) {
                    serviceStatistics {
                        dashboardPerformedWork(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDashboardWorkedHours(
        input: DashboardWorkedHoursInput,
        fragment: string,
    ) {
        return this.query<DashboardWorkedHoursObject[]>(
            `
                query($input: DashboardWorkedHoursInput!) {
                    serviceStatistics {
                        dashboardWorkedHours(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDashboardWorkersWorkedHours(
        input: DashboardWorkersWorkedHoursInput,
        fragment: string,
    ) {
        return this.query<DashboardWorkersWorkedHoursObject[]>(
            `
                query($input: DashboardWorkersWorkedHoursInput!) {
                    serviceStatistics {
                        dashboardWorkersWorkedHours(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialBmStatistics(
        input: SocialBmStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialBmStatisticsObject[]>(
            `
                query($input: SocialBmStatisticsGetInput!) {
                    serviceStatistics {
                        socialBm(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialBmCalculatedData(
        input: SocialBmStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialBmCalculatedDataObject>(
            `
                query($input: SocialBmStatisticsGetInput!) {
                    serviceStatistics {
                        socialBmCalculatedData(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialWarehouseMaterialStatistics(
        input: SocialWarehouseMaterialStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialWarehouseMaterialStatisticsObject[]>(
            `
                query($input: SocialWarehouseMaterialStatisticsGetInput!) {
                    serviceStatistics {
                        socialWarehouseMaterial(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialDependenceStatistics(
        input: SocialDependenceStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialDependenceStatisticsObject[]>(
            `
                query($input: SocialDependenceStatisticsGetInput!) {
                    serviceStatistics {
                        socialDependence(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialDietStatistics(
        input: SocialDietStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialDietStatisticsObject[]>(
            `
                query($input: SocialDietStatisticsGetInput!) {
                    serviceStatistics {
                        socialDiet(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialWorkerEducationStatistics(
        input: SocialWorkerEducationStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialWorkerEducationStatisticsObject[]>(
            `
                query($input: SocialWorkerEducationStatisticsGetInput!) {
                    serviceStatistics {
                        socialWorkerEducation(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialMpsvWorkersStatistics(
        input: SocialMpsvStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialMpsvWorkerStatisticsObject>(
            `
                query($input: SocialMpsvStatisticsGetInput!) {
                    serviceStatistics {
                        socialMpsvWorkers(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialMpsvClientsStatistics(
        input: SocialMpsvStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialMpsvClientStatisticsObject>(
            `
                query($input: SocialMpsvStatisticsGetInput!) {
                    serviceStatistics {
                        socialMpsvClients(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialMpsvAnnualReportStatistics(
        input: SocialMpsvStatisticsGetInput,
        fragment: string,
    ) {
        return this.query<SocialMpsvAnnualReportStatisticsObject>(
            `
                query($input: SocialMpsvStatisticsGetInput!) {
                    serviceStatistics {
                        socialMpsvAnnualReport(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialFinancialDepositClientStatistics(
        input: SocialServiceStatisticsFinancialDepositClientGetInput,
        fragment: string,
    ) {
        return this.query<SocialServiceFinancialDepositClientStatisticsObject>(
            `
                query($input: SocialServiceStatisticsFinancialDepositClientGetInput!) {
                    serviceStatistics {
                        socialFinancialDepositClient(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getSocialFinancialDepositsClientsSummaryStatistics(
        input: SocialServiceStatisticsFinancialDepositsClientsSummaryGetInput,
        fragment: string,
    ) {
        return this.query<SocialServiceFinancialDepositsClientsSummaryStatisticsObject>(
            `
                query($input: SocialServiceStatisticsFinancialDepositsClientsSummaryGetInput!) {
                    serviceStatistics {
                        socialFinancialDepositsClientsSummary(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getServiceWorksStatistics(
        input: ServiceStatisticsWorksGetInput,
        fragment: string = worksStatisticsFrag,
    ) {
        return this.query<ServiceStatisticsWorksObject[]>(
            `
                query($input: ServiceStatisticsWorksGetInput!) {
                    serviceStatistics {
                        works(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getAchoServiceWorksStatistics(
        input: ServiceStatisticsWorksGetInput,
        fragment: string = worksStatisticsFrag,
    ) {
        return this.query<ServiceStatisticsWorksObject[]>(
            `
                query($input: ServiceStatisticsWorksGetInput!) {
                    serviceStatistics {
                        achoWorks(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getServiceWorkersStatistics(
        input: ServiceStatisticsGetInput,
        fragment: string = workersStatisticsFrag,
    ) {
        return this.query<ServiceStatisticsWorkersObject[]>(
            `
                query($input: ServiceStatisticsGetInput!) {
                    serviceStatistics {
                        workers(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getPalliativePeriodProcedures(
        input: ServiceStatisticsGetInput,
        fragment: string = palliativePeriodStatisticsFrag,
    ) {
        return this.query<PalliativePeriodProceduresObject[]>(
            `
                query($input: ServiceStatisticsGetInput!) {
                    serviceStatistics {
                        palliativePeriodProcedures(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getProcedureStatisticReports(
        input: ProcedureStatisticsReportsGetInput,
        fragment: string,
    ) {
        return this.query<ProcedureStatisticReportObject[]>(
            `
                query($input: ProcedureStatisticsReportsGetInput!) {
                    serviceStatistics {
                        getProcedureReports(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteProcedureStatisticReport(
        input: ProcedureStatisticsReportsDeleteInput,
        fragment: string = 'ok',
    ) {
        return this.mutate<OkObject>(
            `
                mutation($input: ProcedureStatisticsReportsDeleteInput!) {
                    serviceStatistics {
                        deleteProcedureReport(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async upsertProcedureStatisticReports(
        input: ProcedureStatisticsReportsUpsertInput,
        fragment: string,
    ) {
        return this.mutate<ProcedureStatisticReportObject>(
            `
                mutation($input: ProcedureStatisticsReportsUpsertInput!) {
                    serviceStatistics {
                        upsertProcedureReports(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getServiceAllowStatistics(
        input: AllowStatisticGetInput,
        fragment: string,
    ) {
        return this.query<AllowStatisticObject[]>(
            `
                query($input: AllowStatisticGetInput!) {
                    serviceStatistics {
                        getAllowed(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async upsertServiceAllowStatistics(
        input: AllowStatisticUpsertInput,
        fragment: string = 'ok',
    ) {
        return this.mutate<ProcedureStatisticReportObject>(
            `
                mutation($input: AllowStatisticUpsertInput!) {
                    serviceStatistics {
                        upsertAllowed(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async exportSocialDeceasedClientInvitation(
        input: SocialDeceasedClientInvitationExportInput,
        fragment: string,
    ) {
        return this.query<FileDownloadObject>(
            `
                query($input: SocialDeceasedClientInvitationExportInput!) {
                    serviceStatistics {
                        socialDeceasedClientInvitationExport(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
